import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addOrgPhoneNumber, createOrg } from '../api/org';
import { getIsExec } from '../api/user';
import { useOrgs } from '../hooks/useOrgs';
import { useToast } from '../hooks/useToast';
import { useLanguage } from '../i18n/LanguageProvider';
import { DabsiApp } from '../types/App';
import { Org } from '../types/Org';
import { ToastStatus } from '../types/Status';
import { FormContainer } from './shared/FormContainer';
import { PhoneNumberInput } from './shared/PhoneNumberInput';
import { SkeletonList } from './shared/Skeleton';
import { Tab, TabView } from './shared/TabView';
import { StyledButton } from './shared/StyledButton';

const styles = {
  heading1: {
    color: '#333',
    borderBottom: '2px solid #333',
    paddingBottom: '10px',
  },
  heading2: {
    color: '#333',
  },
  list: {
    listStyleType: 'none' as const,
    padding: 0,
  },
  listItem: {
    cursor: 'pointer',
    marginBottom: '10px',
    backgroundColor: 'white',
    padding: '10px',
    borderRadius: '4px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  link: {
    color: '#1a73e8',
    textDecoration: 'none',
    fontWeight: 'bold' as const,
  },
  input: {
    padding: '8px 12px',
    border: '1px solid #ccc',
    borderRadius: '8px',
    fontSize: '14px',
    width: '100%',
    boxSizing: 'border-box' as const,
  },
  select: {
    padding: '8px 12px',
    border: '1px solid #ccc',
    borderRadius: '8px',
    fontSize: '14px',
    width: '100%',
    boxSizing: 'border-box' as const,
    backgroundColor: '#fff',
  },
  textarea: {
    padding: '8px 12px',
    border: '1px solid #ccc',
    borderRadius: '8px',
    fontSize: '14px',
    width: '100%',
    boxSizing: 'border-box' as const,
    fontFamily: 'inherit',
    resize: 'vertical' as const,
  },
};

export const HomePage: React.FC = () => {
  const { messages } = useLanguage();
  const navigate = useNavigate();
  const { orgs, fetchOrgs, fetchOrgPhoneNumbers } = useOrgs(false);
  const [isExec, setIsExec] = useState<boolean | null>(null);
  const checkedExec = useRef(false);
  const storedApp = localStorage.getItem('selectedApp') as DabsiApp;

  useEffect(() => {
    if (checkedExec.current) return;
    checkedExec.current = true;
    const fetchIsExec = async () => {
      setIsExec(await getIsExec());
    };
    fetchIsExec();
  }, [checkedExec]);

  const handleClickOrg = useCallback(
    (orgId: number) => {
      const app = storedApp || 'clientell';
      navigate(`/${app}/orgs/${orgId}`);
    },
    [navigate, storedApp]
  );

  const renderYourOrgs = () => {
    return (
      <>
        <h2 style={styles.heading2}>
          {messages.home.yourOrgs}
          {orgs && orgs.length > 0 ? ` (${orgs.length})` : ''}
        </h2>
        <OrganizationList orgs={orgs} handleClick={handleClickOrg} />
      </>
    );
  };

  const tabs: Tab[] = [
    {
      name: '',
      title: messages.home.viewOrgs,
      content: renderYourOrgs(),
    },
    {
      name: 'add',
      title: messages.home.addOrg,
      content: (
        <>
          <h2 style={styles.heading2}>{messages.home.addOrg}</h2>
          <AddOrganization onSuccess={fetchOrgs} />
        </>
      ),
    },
    {
      name: 'manage',
      title: messages.home.manageOrg,
      content: (
        <>
          <h2 style={styles.heading2}>{messages.home.addPhoneNumbersToOrg}</h2>
          <AddPhoneNumbersToOrg
            onSuccess={(orgId: number) => fetchOrgPhoneNumbers(orgId)}
          />
        </>
      ),
    },
  ];

  if (isExec === null) {
    return (
      <div style={{ margin: '40px' }}>
        <SkeletonList length={1} />
      </div>
    );
  }

  if (isExec) {
    return (
      <div style={{ paddingTop: '32px', paddingLeft: '24px' }}>
        <TabView tabs={tabs} />
      </div>
    );
  }

  return <div style={{ padding: '0 40px' }}>{renderYourOrgs()}</div>;
};

type AddOrganizationProps = {
  onSuccess: () => void;
};

const AddOrganization = ({ onSuccess }: AddOrganizationProps) => {
  const { messages } = useLanguage();
  const [name, setName] = useState('');
  const [wabaId, setWabaId] = useState('');
  const [description, setDescription] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [addOrganizationStatus, setAddOrganizationStatus] =
    useState<ToastStatus>(null);

  const disabled = !name || !wabaId || !description || !adminEmail;

  const resetAddOrganizationStatus = useCallback(() => {
    setAddOrganizationStatus(null);
  }, []);

  useToast({
    condition: addOrganizationStatus === 'success',
    status: 'success',
    message: messages.home.orgAddedSuccessfully,
    reset: resetAddOrganizationStatus,
  });
  useToast({
    condition: addOrganizationStatus === 'error',
    status: 'error',
    message: messages.home.errorAddingOrg,
    reset: resetAddOrganizationStatus,
  });

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      try {
        await createOrg(
          {
            name,
            wabaId,
            description,
          },
          adminEmail
        );
        setName('');
        setWabaId('');
        setDescription('');
        setAdminEmail('');
        setAddOrganizationStatus('success');
        onSuccess();
      } catch (error) {
        setAddOrganizationStatus('error');
      }
    },
    [name, wabaId, description, adminEmail, onSuccess]
  );

  return (
    <FormContainer onSubmit={handleSubmit}>
      <input
        style={styles.input}
        value={name}
        onChange={e => setName(e.target.value)}
        placeholder="Organization Name"
        required
      />
      <input
        style={styles.input}
        value={wabaId}
        onChange={e => setWabaId(e.target.value)}
        placeholder="WhatsApp Business Account ID"
        required
      />
      <textarea
        style={styles.textarea}
        rows={3}
        value={description}
        onChange={e => setDescription(e.target.value)}
        placeholder="Description"
        required
      />
      <input
        style={styles.input}
        type="email"
        value={adminEmail}
        onChange={e => setAdminEmail(e.target.value)}
        placeholder="Admin Email"
        required
      />
      <StyledButton
        type="submit"
        variant="primary"
        disabled={disabled}
        fullWidthMobile
      >
        {messages.home.add}
      </StyledButton>
    </FormContainer>
  );
};

type AddPhoneNumbersToOrgProps = {
  onSuccess: (orgId: number) => void;
};

const AddPhoneNumbersToOrg = ({ onSuccess }: AddPhoneNumbersToOrgProps) => {
  const { messages } = useLanguage();
  const { orgs } = useOrgs(false);
  const [selectedOrg, setSelectedOrg] = useState<Org | null>(null);
  const [label, setLabel] = useState('');
  const [country, setCountry] = useState('221');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [addPhoneNumberStatus, setAddPhoneNumberStatus] =
    useState<ToastStatus>(null);

  const disabled = !selectedOrg || !label || !phoneNumber || !phoneNumberId;

  const resetAddPhoneNumberStatus = useCallback(() => {
    setAddPhoneNumberStatus(null);
  }, []);

  useToast({
    condition: addPhoneNumberStatus === 'success',
    status: 'success',
    message: 'Phone number added to organization sucessfully!',
    reset: resetAddPhoneNumberStatus,
    duration: 3000,
  });
  useToast({
    condition: addPhoneNumberStatus === 'error',
    status: 'error',
    message: 'Error adding phone number to organization...',
    reset: resetAddPhoneNumberStatus,
    duration: 3000,
  });

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      try {
        if (!selectedOrg) return;
        const fullPhoneNumber = `${country}${phoneNumber}`;
        await addOrgPhoneNumber(selectedOrg.id, {
          label,
          phoneNumber: fullPhoneNumber,
          phoneNumberId,
        });
        setSelectedOrg(null);
        setLabel('');
        setPhoneNumber('');
        setPhoneNumberId('');
        setAddPhoneNumberStatus('success');
        onSuccess(selectedOrg.id);
      } catch (error) {
        setAddPhoneNumberStatus('error');
      }
    },
    [selectedOrg, label, country, phoneNumber, phoneNumberId, onSuccess]
  );

  if (!orgs) {
    return (
      <p>
        <em>{messages.home.noOrgs}</em>
      </p>
    );
  }

  return (
    <FormContainer onSubmit={handleSubmit}>
      <select
        style={styles.select}
        value={selectedOrg?.id || ''}
        onChange={e => {
          if (!orgs) return;
          const orgId = parseInt(e.target.value);
          setSelectedOrg(orgs.filter(org => org.id === orgId)[0]);
        }}
        required
      >
        <option value="" disabled>
          Select organization
        </option>
        {orgs.map(org => (
          <option key={org.id} value={org.id}>
            {org.name}
          </option>
        ))}
      </select>
      <input
        style={styles.input}
        value={label}
        onChange={e => setLabel(e.target.value)}
        placeholder="Label"
        required
      />
      <PhoneNumberInput
        country={country}
        setCountry={setCountry}
        phoneNumber={phoneNumber}
        setPhoneNumber={setPhoneNumber}
      />
      <input
        style={styles.input}
        value={phoneNumberId}
        onChange={e => setPhoneNumberId(e.target.value)}
        placeholder="Phone Number ID"
        required
      />
      <StyledButton
        type="submit"
        variant="primary"
        disabled={disabled}
        fullWidthMobile
      >
        Add Phone Number
      </StyledButton>
    </FormContainer>
  );
};

type OrganizationListProps = {
  orgs: Org[] | null;
  handleClick: (orgId: number) => void;
};

const OrganizationList = ({ orgs, handleClick }: OrganizationListProps) => {
  const { messages } = useLanguage();

  if (!orgs) {
    return <SkeletonList length={3} />;
  }

  if (orgs.length === 0) {
    return (
      <p style={{ color: '#64748b', fontStyle: 'italic' }}>
        {messages.home.noOrgs}
      </p>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        maxWidth: 480,
      }}
    >
      {orgs.map(org => (
        <button
          key={org.id}
          type="button"
          onClick={() => handleClick(org.id)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '14px 18px',
            border: '1px solid #eef0f6',
            borderRadius: 12,
            background: '#fff',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 15,
            fontWeight: 600,
            color: '#0f172a',
            textAlign: 'left',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = '#d0bfff';
            e.currentTarget.style.boxShadow =
              '0 2px 8px rgba(107, 70, 227, 0.08)';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = '#eef0f6';
            e.currentTarget.style.boxShadow = 'none';
            e.currentTarget.style.transform = 'none';
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: '#f1f0ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6b46e3',
              fontSize: 16,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {org.name.charAt(0).toUpperCase()}
          </div>
          {org.name}
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            style={{ marginLeft: 'auto', color: '#cbd5e1', flexShrink: 0 }}
          >
            <path
              d="M6 4l4 4-4 4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      ))}
    </div>
  );
};
